import { useMemo } from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { SURFACE } from "../common/designTokens";
import { RouteDayRow, RouteDayStop } from "../../types/pivot.type";
import { PV } from "./pivotTokens";
import { buildTimeline, TimelineSegmentKind } from "./routeTimeline";

/** Timeline colours — all from the shared surface / pivot palette. */
const LUNCH_FILL = `repeating-linear-gradient(45deg, ${SURFACE.txDisabled} 0 3px, ${PV.line2} 3px 6px)`;
const SEG_STYLE: Record<TimelineSegmentKind, object> = {
  leave: { bgcolor: PV.line2, border: `1px dashed ${SURFACE.txDisabled}` },
  back: { bgcolor: PV.line2, border: `1px dashed ${SURFACE.txDisabled}` },
  gap: { bgcolor: SURFACE.txDisabled },
  gap_flag: { bgcolor: PV.leftover },
  lunch: { background: LUNCH_FILL },
  app: { bgcolor: PV.indigo },
  app_long: { bgcolor: PV.waste },
};

const LEGEND: { label: string; sx: object; thin?: boolean }[] = [
  { label: "ช่วงห่างปกติ", sx: SEG_STYLE.gap },
  { label: "ช่วงห่างผิดปกติ", sx: SEG_STYLE.gap_flag },
  { label: "พักเที่ยง", sx: SEG_STYLE.lunch },
  { label: "ออกสาขา / กลับตรวจนับ", sx: SEG_STYLE.leave },
  { label: "ทำรายการในแอป", sx: SEG_STYLE.app, thin: true },
  { label: "ทำรายการนาน", sx: SEG_STYLE.app_long, thin: true },
];

const GUTTER = 110;
const TRACK = { callout: 0, travel: 20, app: 58, marker: 92, hour: 126, height: 146 };

/**
 * The day on one horizontal time axis (board A2): gaps between shops on the upper track,
 * app time on the lower one, branch checkout / count start as markers. Plain positioned
 * boxes — minutes map to percentages, so it reflows with the card.
 */
export default function PivotRouteDayTimeline({
  header,
  stops,
}: {
  header: Pick<RouteDayRow, "date" | "leave_at" | "last_left_at" | "count_started_at">;
  stops: RouteDayStop[];
}) {
  const t = useMemo(() => buildTimeline(header, stops), [header, stops]);
  const span = t.windowEnd - t.windowStart;
  const pct = (m: number) => `${((m - t.windowStart) / span) * 100}%`;
  const width = (a: number, b: number) => `${(Math.max(0, b - a) / span) * 100}%`;

  const hours: number[] = [];
  for (let h = Math.ceil(t.windowStart / 60); h * 60 <= t.windowEnd; h++) hours.push(h);

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1.5 }}>
        {LEGEND.map((l) => (
          <Box
            key={l.label}
            sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 12, color: PV.tx2 }}
          >
            <Box
              sx={{
                width: l.thin ? 4 : 18,
                height: l.thin ? 14 : 10,
                borderRadius: "3px",
                boxSizing: "border-box",
                ...l.sx,
              }}
            />
            {l.label}
          </Box>
        ))}
      </Box>

      <Box sx={{ overflowX: "auto" }}>
        <Box sx={{ position: "relative", height: TRACK.height, ml: `${GUTTER}px`, minWidth: 640 }}>
          {[
            { top: TRACK.travel + 5, text: "เดินทาง / งานในร้าน" },
            { top: TRACK.app + 3, text: "ทำรายการ" },
            { top: TRACK.marker + 2, text: "เหตุการณ์" },
          ].map((l) => (
            <Typography
              key={l.text}
              sx={{
                position: "absolute",
                left: -GUTTER,
                width: GUTTER - 10,
                top: l.top,
                fontSize: 12,
                color: PV.tx2,
                textAlign: "right",
              }}
            >
              {l.text}
            </Typography>
          ))}

          {hours.map((h) => (
            <Box key={h}>
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  bottom: 20,
                  left: pct(h * 60),
                  borderLeft: `1px dashed ${PV.line}`,
                }}
              />
              <Typography
                sx={{
                  position: "absolute",
                  top: TRACK.hour,
                  left: pct(h * 60),
                  transform: "translateX(-50%)",
                  fontSize: 11,
                  color: PV.tx2,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {String(h % 24).padStart(2, "0")}:00
              </Typography>
            </Box>
          ))}

          {t.segments.map((s, i) => (
            <Tooltip key={i} title={s.tip} arrow placement="top">
              <Box
                sx={{
                  position: "absolute",
                  top: s.track === "travel" ? TRACK.travel : TRACK.app,
                  height: s.track === "travel" ? 26 : 22,
                  left: pct(s.start),
                  width: width(s.start, s.end),
                  minWidth: 2,
                  borderRadius: s.track === "travel" ? "3px" : "1px",
                  boxSizing: "border-box",
                  cursor: "default",
                  ...SEG_STYLE[s.kind],
                  "&:hover": { outline: `2px solid ${PV.indigoBorder}`, zIndex: 1 },
                }}
              />
            </Tooltip>
          ))}

          {t.callouts.map((c, i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                top: TRACK.callout,
                left: pct(c.at),
                transform: "translateX(-50%)",
                fontSize: 11,
                fontWeight: 700,
                color: PV.leftover,
                bgcolor: PV.leftoverTint,
                borderRadius: "999px",
                px: 0.9,
                whiteSpace: "nowrap",
                pointerEvents: "none",
              }}
            >
              {c.text}
            </Box>
          ))}

          {t.markers.map((m) => (
            <Box
              key={m.kind}
              sx={{
                position: "absolute",
                top: TRACK.marker,
                left: pct(m.at),
                transform: "translateX(-50%)",
                fontSize: 11,
                px: 1,
                py: "2px",
                borderRadius: "999px",
                bgcolor: PV.line2,
                color: PV.tx,
                whiteSpace: "nowrap",
              }}
            >
              {m.label}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
